import type { Table } from 'dexie'
import type { BlockyTimeDB } from '@/db/schema'
import { db as defaultDb } from '@/db/schema'
import { LOCAL_TABLES, type LocalTable } from '@/core/sync/serialize'
import { clearSyncState, setLastPulledSeq } from '@/core/repository/syncStateRepository'
import { EXPORT_FORMAT_VERSION, type ExportBundle } from './exportService'
import { logger } from '@/core/logging/logger'

/**
 * Import, in two modes.
 *
 * MERGE keeps whichever version of each row is newer, by `updatedAt`. This is
 * the safe default: importing an old file cannot destroy newer work.
 *
 * REPLACE wipes everything first. It is the right answer when restoring onto a
 * fresh device, and catastrophic otherwise -- so it is confirmed separately and
 * always preceded by an automatic export.
 *
 * Everything imported is marked `_dirty`, so it propagates to the other device
 * on the next sync rather than sitting locally in disagreement.
 */

export type ImportMode = 'merge' | 'replace'

export interface ImportResult {
  mode: ImportMode
  inserted: number
  updated: number
  skipped: number
}

type AnyRow = Record<string, unknown> & { id: string; updatedAt: number }

function tableOf(db: BlockyTimeDB, name: LocalTable): Table<AnyRow, string> {
  return db[name] as unknown as Table<AnyRow, string>
}

export class ImportError extends Error {}

/** Validates before touching anything. A half-applied import is unrecoverable. */
export function parseBundle(text: string): ExportBundle {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    throw new ImportError('That file is not valid JSON.')
  }

  if (typeof parsed !== 'object' || parsed === null) {
    throw new ImportError('That file does not look like a Time Keeper export.')
  }

  const bundle = parsed as Partial<ExportBundle>

  if (typeof bundle.formatVersion !== 'number') {
    throw new ImportError('That file does not look like a Time Keeper export.')
  }

  if (bundle.formatVersion > EXPORT_FORMAT_VERSION) {
    throw new ImportError(
      `This file was written by a newer version of Time Keeper (format ${bundle.formatVersion}). Update the app first.`,
    )
  }

  if (typeof bundle.tables !== 'object' || bundle.tables === null) {
    throw new ImportError('That export contains no data.')
  }

  return bundle as ExportBundle
}

function isUsableRow(row: unknown): row is AnyRow {
  if (typeof row !== 'object' || row === null) return false
  const r = row as Record<string, unknown>
  return (
    typeof r['id'] === 'string' &&
    r['id'].length > 0 &&
    typeof r['updatedAt'] === 'number' &&
    Number.isFinite(r['updatedAt'])
  )
}

export interface ImportPreview {
  /** Usable rows in the file. */
  fileRows: number
  /** Live blocks in the file. */
  fileEntries: number
  /** Live blocks already here. */
  localEntries: number
  /** Rows that would be added. */
  wouldInsert: number
  /** Rows the file has a newer version of. */
  wouldUpdate: number
  /** Rows already current, or older than what is here. */
  wouldSkip: number
  /**
   * Rows the file shows as live but which are deleted here, more recently.
   *
   * Merge deliberately will NOT bring these back -- the deletion is the newer
   * fact. It is the single most surprising thing about merge, so it is counted
   * and surfaced rather than left as a silent no-op.
   */
  blockedByDeletion: number
}

/**
 * Works out what an import would do, without doing it.
 *
 * Merge is frequently a no-op -- importing a file you just exported changes
 * nothing, correctly -- and without a preview that is indistinguishable from
 * the feature being broken.
 */
export async function previewImport(
  bundle: ExportBundle,
  db: BlockyTimeDB = defaultDb,
): Promise<ImportPreview> {
  const preview: ImportPreview = {
    fileRows: 0,
    fileEntries: 0,
    localEntries: 0,
    wouldInsert: 0,
    wouldUpdate: 0,
    wouldSkip: 0,
    blockedByDeletion: 0,
  }

  preview.localEntries = await db.entries.filter((e) => e.deletedAt === null).count()

  for (const name of LOCAL_TABLES) {
    const incoming = (bundle.tables[name] ?? []).filter(isUsableRow)
    preview.fileRows += incoming.length

    if (name === 'entries') {
      preview.fileEntries += incoming.filter((r) => r['deletedAt'] === null).length
    }

    const table = tableOf(db, name)

    for (const row of incoming) {
      const existing = await table.get(row.id)

      if (!existing) {
        preview.wouldInsert += 1
      } else if (row.updatedAt > existing.updatedAt) {
        preview.wouldUpdate += 1
      } else {
        preview.wouldSkip += 1
        if (row['deletedAt'] === null && existing['deletedAt'] !== null) {
          preview.blockedByDeletion += 1
        }
      }
    }
  }

  return preview
}

export async function importBundle(
  bundle: ExportBundle,
  mode: ImportMode,
  db: BlockyTimeDB = defaultDb,
): Promise<ImportResult> {
  const result: ImportResult = { mode, inserted: 0, updated: 0, skipped: 0 }

  for (const name of LOCAL_TABLES) {
    const incoming = (bundle.tables[name] ?? []).filter(isUsableRow)
    const table = tableOf(db, name)

    await db.transaction('rw', table, async () => {
      if (mode === 'replace') await table.clear()

      for (const raw of incoming) {
        const row: AnyRow = { ...raw, _dirty: 1 }

        if (mode === 'replace') {
          await table.put(row)
          result.inserted += 1
          continue
        }

        const existing = await table.get(row.id)

        if (!existing) {
          await table.put(row)
          result.inserted += 1
        } else if (row.updatedAt > existing.updatedAt) {
          await table.put(row)
          result.updated += 1
        } else {
          // The local copy is the same age or newer. Importing an older file
          // must never undo more recent work.
          result.skipped += 1
        }
      }
    })
  }

  logger.info(
    'db',
    `Import (${mode}): ${result.inserted} inserted, ${result.updated} updated, ${result.skipped} skipped`,
  )
  return result
}

/**
 * Wipes every synced table and resets the state that depends on them.
 *
 * The device keeps its identity and its sync token -- erasing data must not
 * silently sign you out. But two other pieces of syncState describe the data
 * that has just been deleted, and leaving them behind strands the device:
 *
 *   lastPulledSeq  The pull cursor. Left alone, the next sync asks only for
 *                  rows NEWER than the cursor, so everything already on the
 *                  server is never re-requested. The data is safe there and
 *                  simply never comes back.
 *
 *   seeded         The "starter catalogue already created" marker. Left alone,
 *                  the app declines to recreate the event types, so the picker
 *                  stays permanently empty and nothing can be marked.
 *
 * Both were missed the first time round, and together they turned "erase and
 * restore" into "erase".
 */
export async function eraseAllData(db: BlockyTimeDB = defaultDb): Promise<void> {
  for (const name of LOCAL_TABLES) {
    await tableOf(db, name).clear()
  }

  await setLastPulledSeq(0, db)
  await clearSyncState('seeded', db)

  logger.warn('db', 'All local data erased; pull cursor and seed marker reset')
}

/**
 * Asks the server for everything again.
 *
 * Resets the pull cursor so the next sync re-downloads the full history rather
 * than only what has changed since. The recovery path when a device has lost
 * its data but the server still has it.
 */
export async function resetPullCursor(db: BlockyTimeDB = defaultDb): Promise<void> {
  await setLastPulledSeq(0, db)
  logger.info('sync', 'Pull cursor reset; the next sync will re-download everything')
}

export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new ImportError('Could not read that file.'))
    reader.readAsText(file)
  })
}
